import React, { useEffect, useRef, useState } from 'react';
import {
  Terminal as TerminalIcon, Banknote, DatabaseZap, KeyRound, Droplets,
  ShieldAlert, RefreshCw, Users, Lock, Bot,
} from 'lucide-react';
import { useT } from '../../i18n';

const ACTIONS = [
  { n: 1, id: 'drain-all', label: 'Desviar R$1 de CADA conta (robô)', icon: Bot },
  { n: 2, id: 'pix', label: 'Realizar PIX (roubar dinheiro)', icon: Banknote },
  { n: 3, id: 'export-data', label: 'Exportar dados sensíveis (dump completo)', icon: DatabaseZap },
  { n: 4, id: 'dump-secrets', label: 'Extrair segredos / credenciais', icon: KeyRound },
  { n: 5, id: 'drain', label: 'Drenar conta (PIX máximo)', icon: Droplets },
];

function formatResult(id, res) {
  if (!res || res.ok === false) {
    return [{ level: 'critical', text: `✗ falhou: ${res?.error || 'sem resposta'}` }];
  }
  if (id === 'drain-all') {
    return [{
      level: res.total > 0 ? 'success' : 'warn',
      text: `${res.total > 0 ? '✓' : '⚠'} robô desviou R$ ${Number(res.total || 0).toFixed(2)} `
        + `de ${res.drained_ok}/${res.accounts} contas → ${res.to}`,
    }];
  }
  if (id === 'pix' || id === 'drain') {
    return [{
      level: res.ok ? 'success' : 'warn',
      text: `${res.ok ? '✓' : '⚠'} PIX R$ ${Number(res.amount).toLocaleString('pt-BR')} → ${res.to} `
        + `(conta ${res.from_account}, HTTP ${res.http_status})`,
    }];
  }
  if (id === 'export-data') {
    return [{ level: 'success', text: `✓ ${res.records_leaked || 0} registros exfiltrados (nome, CPF, saldo, chave PIX)` }];
  }
  if (id === 'dump-secrets') {
    const secs = res.secrets || [];
    if (!secs.length) return [{ level: 'warn', text: '⚠ nenhum segredo capturado (callback?)' }];
    return secs.map((s) => ({ level: 'success', text: `✓ ${Object.entries(s).map(([k, v]) => `${k}=${v}`).join(' ')}` }));
  }
  return [{ level: 'success', text: '✓ concluído' }];
}

function Console({ evd }) {
  const { t } = useT();
  const { runPostExploit, running } = evd;
  const [lines, setLines] = useState([
    { level: 'info', text: 'root@evildog:~# sessão estabelecida — você JÁ está dentro do sistema.' },
    { level: 'muted', text: 'Escolha uma ação (digite o número e Enter) ou clique nos botões:' },
    ...ACTIONS.map((a) => ({ level: 'menu', text: `  [${a.n}] ${a.label}` })),
  ]);
  const [input, setInput] = useState('');
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [lines]);

  const exec = async (id) => {
    const act = ACTIONS.find((a) => a.id === id);
    setLines((l) => [...l, { level: 'in', text: `root@evildog:~# ${id}  (${act?.label || id})` },
      { level: 'muted', text: 'executando…' }]);
    const res = await runPostExploit(id);
    setLines((l) => [...l.filter((x) => x.text !== 'executando…'), ...formatResult(id, res)]);
  };

  const onKey = (e) => {
    if (e.key !== 'Enter') return;
    const v = input.trim().toLowerCase();
    setInput('');
    if (!v) return;
    const byNum = ACTIONS.find((a) => String(a.n) === v);
    const byId = ACTIONS.find((a) => a.id === v || a.id.startsWith(v));
    const act = byNum || byId;
    if (act) exec(act.id);
    else setLines((l) => [...l, { level: 'in', text: `root@evildog:~# ${v}` },
      { level: 'critical', text: `comando desconhecido: ${v}` }]);
  };

  const color = (lv) => ({
    success: 'text-green-400', critical: 'text-red-400', warn: 'text-amber-400',
    in: 'text-slate-200', menu: 'text-cyan-300', muted: 'text-slate-500', info: 'text-green-400',
  }[lv] || 'text-slate-400');

  return (
    <div className="rounded-xl border border-red-500/30 bg-[#080b10] overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-[#1e2733]">
        <div className="flex items-center gap-2 text-red-400 text-sm font-bold">
          <TerminalIcon className="w-4 h-4" /> {t('evd.pe_console')}
        </div>
        <span className="text-[10px] tracking-widest text-slate-500 uppercase">{t('evd.pe_inside_tag')}</span>
      </div>
      <div ref={ref} className="evd-scroll p-4 overflow-y-auto text-[13px] leading-relaxed" style={{ height: '230px' }}>
        {lines.map((l, i) => <div key={i} className={color(l.level)}>{l.text}</div>)}
      </div>
      <div className="flex items-center gap-2 px-4 py-2 border-t border-[#1e2733]">
        <span className="text-red-400 font-bold">root@evildog:~#</span>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          placeholder="1-4 ou nome da ação…"
          className="flex-1 bg-transparent outline-none text-slate-100 text-sm placeholder:text-slate-600"
          autoFocus
        />
      </div>
      <div className="flex flex-wrap gap-2 px-4 pb-4">
        {ACTIONS.map((a) => {
          const Icon = a.icon;
          const busy = running === `pe:${a.id}`;
          return (
            <button key={a.id} onClick={() => exec(a.id)} disabled={busy}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs transition-colors
                ${busy ? 'border-amber-500/40 text-amber-300 cursor-wait'
                  : 'border-red-500/30 bg-red-500/5 text-red-300 hover:bg-red-500/15'}`}>
              <Icon className="w-3.5 h-3.5" /> [{a.n}] {a.label.split(' (')[0]}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const mask = (s, keep = 3) => (s && s.length > keep ? '•'.repeat(Math.max(0, s.length - keep)) + s.slice(-keep) : s);

function LootReport({ evd }) {
  const { t } = useT();
  const { loot, refreshLoot } = evd;
  const s = loot?.summary || {};
  const [reveal, setReveal] = useState(false);
  const tiles = [
    { label: 'Registros roubados', value: s.records || 0, icon: Users, color: 'text-cyan-400' },
    { label: 'Valor em contas', value: `R$ ${Number(s.accounts_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, icon: Banknote, color: 'text-green-400' },
    { label: 'Segredos', value: (loot?.secrets || []).length, icon: KeyRound, color: 'text-amber-400' },
    { label: 'PIX desviado', value: `R$ ${Number(s.pix_stolen_total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, icon: Droplets, color: 'text-red-400' },
  ];
  const records = loot?.records || [];

  return (
    <div className="rounded-xl border border-[#1e2733] bg-[#0f151d] p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-red-400" />
          <h3 className="font-bold text-slate-100">{t('evd.pe_report_title')}</h3>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setReveal((r) => !r)} className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200">
            <Lock className="w-3.5 h-3.5" /> {reveal ? t('evd.mask') : t('evd.reveal')}
          </button>
          <button onClick={refreshLoot} className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200">
            <RefreshCw className="w-3.5 h-3.5" /> {t('evd.refresh')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {tiles.map((t) => {
          const Icon = t.icon;
          return (
            <div key={t.label} className="rounded-lg border border-[#1e2733] bg-[#0b1017] p-3">
              <div className="flex items-center gap-2 text-[11px] text-slate-500 uppercase tracking-wider">
                <Icon className={`w-3.5 h-3.5 ${t.color}`} /> {t.label}
              </div>
              <div className={`text-xl font-bold mt-1 tabular-nums ${t.color}`}>{t.value}</div>
            </div>
          );
        })}
      </div>

      {/* secrets */}
      {(loot?.secrets || []).length > 0 && (
        <div className="mb-4 rounded-lg border border-red-500/20 bg-red-500/5 p-3">
          <div className="text-[11px] uppercase tracking-widest text-red-400 mb-1">Credenciais / segredos</div>
          {loot.secrets.map((sec, i) => (
            <div key={i} className="text-[13px] font-mono text-red-300 break-all">
              {Object.entries(sec).map(([k, v]) => `${k} = ${reveal ? v : mask(v)}`).join('  ')}
            </div>
          ))}
        </div>
      )}

      {/* records table */}
      <div className="overflow-x-auto rounded-lg border border-[#1e2733]">
        <table className="w-full text-[13px] min-w-[560px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-[#1e2733]">
              <th className="text-left px-3 py-2">{t('evd.col_name')}</th>
              <th className="text-left px-3 py-2">{t('evd.col_cpf')}</th>
              <th className="text-left px-3 py-2">{t('evd.col_pass')}</th>
              <th className="text-left px-3 py-2">{t('evd.col_balance')}</th>
              <th className="text-left px-3 py-2">{t('evd.col_bank')}</th>
              <th className="text-left px-3 py-2">{t('evd.col_pixkey')}</th>
              <th className="text-left px-3 py-2">{t('evd.col_mfa')}</th>
            </tr>
          </thead>
          <tbody>
            {records.length === 0 && (
              <tr><td colSpan="7" className="px-3 py-6 text-center text-slate-600">
                nenhum dado ainda — rode a pipeline ou exporte dados no console acima
              </td></tr>
            )}
            {records.map((r, i) => (
              <tr key={i} className="border-b border-[#131b25] last:border-0">
                <td className="px-3 py-2 text-slate-200">{r.nome}</td>
                <td className="px-3 py-2 font-mono text-red-300">{reveal ? r.cpf : mask(r.cpf)}</td>
                <td className="px-3 py-2 font-mono text-red-400 font-bold">{reveal ? (r.senha || '—') : mask(r.senha, 2)}</td>
                <td className="px-3 py-2 font-mono text-green-400">{r.saldo}</td>
                <td className="px-3 py-2 text-slate-400">{r.banco}</td>
                <td className="px-3 py-2 font-mono text-cyan-300 text-xs">{reveal ? r.chave_pix : mask(r.chave_pix, 5)}</td>
                <td className="px-3 py-2 text-xs">{r.mfa
                  ? <span className="text-green-400">🔒 {t('evd.mfa_on')}</span>
                  : <span className="text-red-400">{t('evd.mfa_off')}</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function PostExploitPanel({ evd }) {
  const { t } = useT();
  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-[#1e2733] bg-gradient-to-r from-[#1a0f12] to-[#0f151d] p-4">
        <div className="text-sm text-slate-300">
          <span className="text-red-400 font-bold">{t('evd.pe_inside')}</span> {t('evd.pe_desc')}
        </div>
        <div className="text-xs text-slate-500 mt-1.5">
          O robô <span className="text-slate-300">"Desviar R$1 de cada conta"</span> reproduz{' '}
          <span className="text-amber-400">salami slicing</span> — técnica real (ex.: caso Michael Largent,
          2008: ~US$ 50k em micro-desvios, detectados pela monitoração dos bancos).
        </div>
      </div>
      <Console evd={evd} />
      <LootReport evd={evd} />
    </div>
  );
}
